import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

const campusSections = [
  {
    title: "Auditorium",
    images: [
      "/renamed_website_images/auditorium-1.jpg",
      "/renamed_website_images/auditorium-2.jpg",
    ],
    desc: "Our state-of-the-art auditorium is a hub of cultural and academic activities. Equipped with professional lighting and sound systems, it provides an immersive experience for seminars, performances, and ceremonies.",
    color: "#f4c842",
    border: "rgba(244,200,66,0.2)",
  },
  {
    title: "Main Stage",
    images: [
      "/renamed_website_images/infrastructure-section-screenshot.jpg",
      "/renamed_website_images/parade-training.jpg",
    ],
    desc: "Our open-air stage is the centerpiece for cultural programs, skits, and annual functions. Designed for visibility and sound, it supports performances that engage the entire school community.",
    color: "#8b5cf6",
    border: "rgba(139,92,246,0.2)",
  },
  {
    title: "Reception Area",
    images: [
      "/renamed_website_images/infrastructure-lobby-2.jpg",
      "/renamed_website_images/campus-palm-trees.jpg",
    ],
    desc: "The reception area serves as the first point of contact for visitors, parents, and students, staffed by a professional team that ensures smooth communication and assistance for all administrative needs.",
    color: "#22c55e",
    border: "rgba(34,197,94,0.2)",
  },
  {
    title: "Medical Room",
    images: [
      "/renamed_website_images/hostel-room.jpg",
      "/renamed_website_images/infrastructure-section-screenshot.jpg",
    ],
    desc: "The well-equipped medical room is available during school hours, managed by trained personnel. It provides first aid, manages minor injuries, and supports student health and safety.",
    color: "#ef4444",
    border: "rgba(239,68,68,0.2)",
  },
  {
    title: "Sports Activities",
    images: [
      "/renamed_website_images/sports-activities-001.jpg",
      "/renamed_website_images/sports-activities-kabaddi-2.jpg",
    ],
    desc: "Our large playground encourages physical activity, sportsmanship, and teamwork. It includes dedicated areas for different games and is an essential part of holistic education at our school.",
    color: "#06b6d4",
    border: "rgba(6,182,212,0.2)",
  },
];

const CampusSection = ({ title, images, desc, color, border }) => {
  const [index, setIndex] = useState(0);

  return (
    <div
      className="glass-card p-6 mb-6 transition-all duration-300"
      style={{ borderColor: border }}
    >
      <div className="flex flex-col md:flex-row items-center gap-8">
        <div
          className="relative w-full md:w-[42%] flex-shrink-0 rounded-xl overflow-hidden"
          style={{ border: `1px solid ${border}` }}
        >
          <img
            src={images[index]}
            alt={title}
            className="w-full object-cover transition-all duration-500"
            style={{ height: "200px" }}
          />
          {images.length > 1 && (
            <>
              <button
                onClick={() => setIndex((value) => Math.max(0, value - 1))}
                disabled={index === 0}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200"
                style={{
                  background: "rgba(2,8,16,0.85)",
                  border: `1px solid ${border}`,
                  color: index === 0 ? "#3a4a6a" : color,
                  cursor: index === 0 ? "not-allowed" : "pointer",
                }}
              >
                <IoIosArrowBack size={14} />
              </button>
              <button
                onClick={() =>
                  setIndex((value) => Math.min(images.length - 1, value + 1))
                }
                disabled={index === images.length - 1}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200"
                style={{
                  background: "rgba(2,8,16,0.85)",
                  border: `1px solid ${border}`,
                  color: index === images.length - 1 ? "#3a4a6a" : color,
                  cursor:
                    index === images.length - 1 ? "not-allowed" : "pointer",
                }}
              >
                <IoIosArrowForward size={14} />
              </button>
            </>
          )}
        </div>

        <div className="flex-1">
          <h3
            className="mb-3 font-bold"
            style={{
              fontFamily: "Cinzel, serif",
              color,
              fontSize: "1rem",
            }}
          >
            {title}
          </h3>
          <p
            className="leading-relaxed"
            style={{
              fontFamily: "Nunito, sans-serif",
              color: "#8899b8",
              fontSize: "0.9rem",
            }}
          >
            {desc}
          </p>
        </div>
      </div>
    </div>
  );
};

const NatureScroll = () => {
  const scrollRef = useRef(null);
  const [pos, setPos] = useState({ left: false, right: true });

  const images = [
    "/renamed_website_images/campus-palm-trees.jpg",
    "/renamed_website_images/infrastructure-section-mobile.jpg",
    "/renamed_website_images/hostel-room.jpg",
    "/renamed_website_images/infrastructure-section-screenshot.jpg",
  ];

  const scroll = (direction) =>
    scrollRef.current?.scrollBy({
      left: direction === "left" ? -280 : 280,
      behavior: "smooth",
    });

  const check = () => {
    const element = scrollRef.current;
    if (!element) return;
    setPos({
      left: element.scrollLeft > 0,
      right: element.scrollLeft + element.clientWidth < element.scrollWidth - 1,
    });
  };

  useEffect(() => {
    check();
    const element = scrollRef.current;
    element?.addEventListener("scroll", check);
    return () => element?.removeEventListener("scroll", check);
  }, []);

  return (
    <div
      className="glass-card p-6 mb-6"
      style={{ borderColor: "rgba(34,197,94,0.2)" }}
    >
      <h3
        className="mb-4 font-bold"
        style={{
          fontFamily: "Cinzel, serif",
          color: "#22c55e",
          fontSize: "1rem",
        }}
      >
        Nature Around Campus
      </h3>
      <div className="relative">
        <button
          onClick={() => scroll("left")}
          disabled={!pos.left}
          className="absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center z-10 transition-all duration-200"
          style={{
            background: "rgba(2,8,16,0.85)",
            border: "1px solid rgba(34,197,94,0.3)",
            color: pos.left ? "#22c55e" : "#3a4a6a",
            cursor: pos.left ? "pointer" : "not-allowed",
          }}
        >
          <IoIosArrowBack size={14} />
        </button>
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto no-scrollbar pb-2 px-10"
        >
          {images.map((src, index) => (
            <img
              key={src}
              src={src}
              alt={`Nature ${index + 1}`}
              className="min-w-[260px] max-w-[260px] object-cover rounded-xl flex-shrink-0"
              style={{
                height: "160px",
                border: "1px solid rgba(34,197,94,0.2)",
              }}
            />
          ))}
        </div>
        <button
          onClick={() => scroll("right")}
          disabled={!pos.right}
          className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center z-10 transition-all duration-200"
          style={{
            background: "rgba(2,8,16,0.85)",
            border: "1px solid rgba(34,197,94,0.3)",
            color: pos.right ? "#22c55e" : "#3a4a6a",
            cursor: pos.right ? "pointer" : "not-allowed",
          }}
        >
          <IoIosArrowForward size={14} />
        </button>
      </div>
      <p
        className="mt-4"
        style={{
          fontFamily: "Nunito, sans-serif",
          color: "#8899b8",
          fontSize: "0.9rem",
          lineHeight: "1.8",
        }}
      >
        Surrounded by lush greenery, our campus offers a serene environment that
        promotes learning and well-being. The natural landscape provides a
        refreshing escape from the classroom and is ideal for walks, reflection,
        and connecting with nature.
      </p>
    </div>
  );
};

const Campusinfo = () => (
  <section className="relative min-h-screen pt-36 md:pt-40 pb-20 px-6 md:px-16 md:pt-[96px] overflow-hidden">
    <div className="mb-6">
      <Link
        to="/facilities"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card transition-all duration-200"
        style={{ fontFamily: "Nunito, sans-serif", color: "#f4c842" }}
      >
        <FaArrowLeft /> Back to Facilities
      </Link>
    </div>

    <h1
      className="text-3xl md:text-5xl font-black mb-3"
      style={{
        fontFamily: "Cinzel, serif",
        color: "#f0f5ff",
        fontWeight: 900,
      }}
    >
      Campus Information
    </h1>
    <div className="cosmic-divider mb-10" />

    <NatureScroll />

    <div>
      {campusSections.map((section) => (
        <CampusSection key={section.title} {...section} />
      ))}
    </div>
  </section>
);

export default Campusinfo;
